import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  Container,
  Card,
  SimpleGrid,
  Flex,
  Icon,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Spinner,
} from '@chakra-ui/react';
import { getTaskMasterList } from '../../../redux/reduxApi/admin/TaskMasterReduxApi';
import { useDispatch } from 'react-redux';
import { getEmployeesList } from '../../../redux/reduxApi/admin/EmployeesReduxApi';
import { getUserDetails } from '../../../../utils/IndexDB/PermissionStore';

const TaskDashboard = () => {
  const dispatch = useDispatch();
  const [userDetails, setUserDetails] = useState(null);
  const [taskLoading, setTaskLoading] = useState(true);
  const [employeeLoading, setEmployeeLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [dashboardData, setDashboardData] = useState({});
  const [employeesData, setEmployeesData] = useState([]);

  useEffect(() => {
    const fetchUser = async () => {
      const user = await getUserDetails();
      setUserDetails(user);
    };

    fetchUser();
  }, []);


  const getModuleDetails = (task) => {
    const modules = ["lead", "deal", "quotation", "company", "contact",];

    for (let key of modules) {
      if (task[key]) {
        return {
          module: key.charAt(0).toUpperCase() + key.slice(1),
          id: task[key],
        };
      }
    }

    return null
  };


  useEffect(() => {
    if (employeesData.length === 0 || !userDetails) return;

    setTaskLoading(true);

    dispatch(getTaskMasterList({ page: 1, size: 100 }))
      .then((response) => {
        let data = response.payload?.data?.tasks || [];

        if (userDetails?.role !== "Admin") {
          data = data.filter(task => task.taskOwner === userDetails?.ROWID);
        }

        const mapped = data.map((task) => {
          const moduleInfo = getModuleDetails(task);

          return {
            ...task,
            module: moduleInfo?.module,
          };
        });

        const finalData = processTasks(mapped);
        setDashboardData(finalData);
      })
      .finally(() => {
        setTaskLoading(false);
      });

  }, [employeesData, userDetails]);


  useEffect(() => {
    setEmployeeLoading(true);

    dispatch(getEmployeesList({ page: 1, size: 100 }))
      .then((res) => {
        setEmployeesData(res.payload?.data?.employees || []);
      })
      .finally(() => {
        setEmployeeLoading(false);
      });
  }, []);


  const parseDate = (dateStr) => {
    if (!dateStr) return null;

    const [day, month, year] = dateStr.split("/");
    return new Date(`${year}-${month}-${day}`);
  };

  const isToday = (date) => {
    const today = new Date();
    const d = parseDate(date);
    if (!d) return false;

    return d.toDateString() === today.toDateString();
  };

  const isOverdue = (date) => {
    const today = new Date();
    const d = parseDate(date);
    if (!d) return false;

    return d < today && d.toDateString() !== today.toDateString();
  };

  const isThisWeek = (date) => {
    const today = new Date();
    const d = parseDate(date);
    if (!d) return false;

    const firstDay = new Date();
    firstDay.setDate(today.getDate() - today.getDay());

    const lastDay = new Date(firstDay);
    lastDay.setDate(firstDay.getDate() + 6);

    return d >= firstDay && d <= lastDay;
  };


  const groupByOwner = (tasks) => {
    const map = {};

    tasks.forEach((task) => {
      const ownerId = task.taskOwner;
      const ownerName = employeeMap[ownerId] || "Unknown";

      if (!map[ownerName]) {
        map[ownerName] = 0;
      }

      map[ownerName]++;
    });

    return Object.keys(map).map((name) => ({
      name,
      count: map[name],
    }));
  };


  const processTasks = (data) => {
    const modules = {
      Lead: [],
      Deal: [],
      Quotation: [],
      Company: [],
      Contact: [],
      All: [],
    };

    data.forEach((task) => {
      const module = task.module;

      // push into module
      if (modules[module]) {
        modules[module].push(task);
      }

      // all tasks
      modules["All"].push(task);
    });

    const result = {};

    Object.keys(modules).forEach((module) => {
      const tasks = modules[module];

      const today = tasks.filter((t) => isToday(t.dueDate));
      const overdue = tasks.filter((t) => isOverdue(t.dueDate));
      const week = tasks.filter((t) => isThisWeek(t.dueDate));

      result[module] = {
        today: groupByOwner(today),
        overdue: groupByOwner(overdue),
        week: groupByOwner(week),
      };
    });

    return result;
  };

  const employeeMap = useMemo(() => {
    const map = {};
    employeesData.forEach(emp => {
      map[emp.ROWID] = `${emp.firstName || ""} ${emp.lastName || ""}`.trim();
    });
    return map;
  }, [employeesData]);


  const calculateTotal = (tasks) => {
    return tasks.reduce((sum, task) => sum + task.count, 0);
  };

  const handleTabChange = (index) => {
    setTabIndex(index);
  };

  const isLoading = taskLoading || employeeLoading;
  if (isLoading) {
    return (
      <Flex height="100vh" justify="center" align="center">
        <Spinner size="xl" color="#011638" />
      </Flex>
    );
  }

  // Helper component for table rendering with optional header styling
  const renderTaskTable = (title, tasks, total, isOverdue = false, headerStyle = 'default') => {
    // Define header styles
    const getHeaderStyles = () => {
      switch (headerStyle) {
        case 'blue':
          return {
            bg: "#c7e9ff",
            textColor: "#25618a",
            iconColor: "#25618a"
          };
        case 'warm':
          return {
            bg: "#f8e5c0",
            textColor: "#dc9d41",
            iconColor: "#dc9d41"
          };
        case 'red':
          return {
            bg: "#edc6bf",
            textColor: "#a33230",
            iconColor: "#a33230"
          };
        case 'gray':
          return {
            bg: "#e2e8f0",
            textColor: "#334155",
            iconColor: "#334155"
          };
        case 'indigo':
          return {
            bg: "#e0e7ff",
            textColor: "#3730a3",
            iconColor: "#3730a3"
          };
        case 'green':
          return {
            bg: "#c8e6d9",
            textColor: "#2d6a4f",
            iconColor: "#2d6a4f"
          };
        default:
          return {
            bg: "transparent",
            textColor: "inherit",
            iconColor: isOverdue ? 'red.500' : 'blue.500'
          };
      }
    };

    const headerStyles = getHeaderStyles();

    return (
      <Card
        variant="outline"
        borderRadius="lg"
        boxShadow="sm"
        height="450px"  // Fixed height for each card
        width="100%"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        {/* Fixed Header Section */}
        {/* Fixed Header Section */}
        <Box
          bg={headerStyles.bg}
          borderTopRadius="lg"
          p={4}
          flexShrink={0}
        >
          <Flex alignItems="center" >
            <Icon
              viewBox="0 0 24 24"
              width="20px"
              height="20px"
              color={headerStyles.iconColor}
              display="flex"
              alignSelf="center"            >
              {isOverdue ? (
                <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
              ) : title.includes("Today") ? (
                <path fill="currentColor" d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
              ) : (
                <path fill="currentColor" d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13h-1v6l5.25 3.15.75-1.23-4.5-2.67z" />
              )}
            </Icon>
            <Heading
              size="md"
              fontWeight="semibold"
              color={headerStyles.textColor}
              ml={2}
              lineHeight="1.2"
              alignSelf="center"
              sx={{ marginBottom: '-3px !important' }}
            >

              {title}
            </Heading>
          </Flex>
        </Box>

        {/* Fixed Table Header Row */}
        <Box
          px={4}
          pt={3}
          pb={2}
          flexShrink={0}
          bg="white"
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          {/* <Flex justify="space-between" align="center">
            <Text fontWeight="semibold" fontSize="18px" color="gray.700  ">
              Task Owner (Tasks)
            </Text>
            <Text fontWeight="semibold" fontSize="18px" color="gray.700 mb={0}">
              Count
            </Text>
          </Flex> */}

          <Flex justify="space-between" align="center">
            <Text
              fontWeight="semibold"
              fontSize="18px"
              color="gray.700"
              sx={{ marginBottom: '-3px  !important' }}
            >
              Task Owner (Tasks)
            </Text>
            <Text
              fontWeight="semibold"
              fontSize="18px"
              color="gray.700"
              sx={{ marginBottom: '-3px !important' }}
            >
              Count
            </Text>
          </Flex>
        </Box>

        {/* Scrollable Content Area - This is where scrolling happens */}
        <Box
          overflowY="auto"
          flex="1"
          minH={0}
          sx={{
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '3px',
              '&:hover': {
                background: '#a8a8a8',
              },
            },
          }}
        >
          <Box p={4}>
            {tasks.length === 0 ? (
              <Text color="gray.500" textAlign="center" py={4}>
                No Data Found
              </Text>
            ) : (
              tasks.map((task, index) => (
                <Box
                  key={index}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={-5}
                  borderBottom={index !== tasks.length - 1 ? "1px solid" : "none"}
                  borderColor="gray.100"
                  _hover={{ bg: 'gray.50' }}
                  transition="background 0.2s"
                >
                  <Text
                    fontSize="18px"
                    color="gray.800"
                    fontWeight="normal"
                    flex="1"
                  >
                    {task.name}
                  </Text>
                  <Text
                    fontSize="18px"
                    color="gray.800"
                    fontWeight="normal"
                    textAlign="right"
                    minW="60px"
                  >
                    {task.count}
                  </Text>
                </Box>
              ))
            )}
          </Box>
        </Box>

        {/* Fixed Total Section */}
        <Box
          borderTop="1px solid"
          borderColor="gray.200"
          bg="white"
          borderBottomRadius="lg"
          flexShrink={0}
          py={3}
          px={4}
        >
          {/* <Flex justify="space-between">
            <Text fontWeight="semibold" fontSize="20px" color="gray.700">
              Total
            </Text>
            <Text fontWeight="semibold" fontSize="20px" color="gray.700">
              {total}
            </Text>
          </Flex> */}

          <Flex justify="space-between">
            <Text
              fontWeight="semibold"
              fontSize="20px"
              color="gray.700"
              sx={{ marginBottom: '-5px !important' }}
            >
              Total
            </Text>
            <Text
              fontWeight="semibold"
              fontSize="20px"
              color="gray.700"
              sx={{ marginBottom: '-5px  !important' }}
            >
              {total}
            </Text>
          </Flex>
        </Box>
      </Card>
    );
  };

  // Render content based on selected tab
  const renderContent = () => {
    switch (tabIndex) {
      case 0:
        return (
          <Box>
                 {/* FOURTH ROW - Green theme */}
            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4} mt={4}>
              {renderTaskTable("Today's Due Task: All Task",
                dashboardData?.All?.today || [],
                calculateTotal(dashboardData?.All?.today || []),
                false,
                'green'
              )}
              {renderTaskTable("Overdue Tasks: All Task",
                dashboardData?.All?.overdue || [],
                calculateTotal(dashboardData?.All?.overdue || []),
                true,
                'green'
              )}
              {renderTaskTable("This Week Due Tasks: All Task",
                dashboardData?.All?.week || [],
                calculateTotal(dashboardData?.All?.week || []),
                false,
                'green'
              )}
            </SimpleGrid>
            {/* FIRST ROW - Blue theme */}
            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
              {renderTaskTable("Today's Due Task: Lead",
                dashboardData?.Lead?.today || [],
                calculateTotal(dashboardData?.Lead?.today || []),
                false,
                'blue'
              )}
              {renderTaskTable("Overdue Tasks: Lead",
                dashboardData?.Lead?.overdue || [],
                calculateTotal(dashboardData?.Lead?.overdue || []),
                true,
                'blue'
              )}
              {renderTaskTable("This Week Due Tasks: Lead",
                dashboardData?.Lead?.week || [],
                calculateTotal(dashboardData?.Lead?.week || []),
                false,
                'blue'
              )}
            </SimpleGrid>

            {/* SECOND ROW - Warm theme */}
            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4} mt={4}>
              {renderTaskTable(
                "Today's Due Task: Deal",
                dashboardData?.Deal?.today || [],
                calculateTotal(dashboardData?.Deal?.today || []),
                false,
                'warm'
              )}
              {renderTaskTable("Overdue Tasks: Deal",
                dashboardData?.Deal?.overdue || [],
                calculateTotal(dashboardData?.Deal?.overdue || []),
                true,
                'warm'
              )}
              {renderTaskTable("This Week Due Tasks: Deal",
                dashboardData?.Deal?.week || [],
                calculateTotal(dashboardData?.Deal?.week || []),
                false,
                'warm'
              )}
            </SimpleGrid>

            {/* THIRD ROW - Red/Coral theme */}
            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4} mt={4}>
              {renderTaskTable("Today's Due Task: Company",
                dashboardData?.Company?.today || [],
                calculateTotal(dashboardData?.Company?.today || []),
                false,
                'red'
              )}
              {renderTaskTable("Overdue Tasks: Company",
                dashboardData?.Company?.overdue || [],
                calculateTotal(dashboardData?.Company?.overdue || []),
                true,
                'red'
              )}
              {renderTaskTable("This Week Due Tasks: Company",
                dashboardData?.Company?.week || [],
                calculateTotal(dashboardData?.Company?.week || []),
                false,
                'red'
              )}
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4} mt={4}>
              {renderTaskTable("Today's Due Task: Contact",
                dashboardData?.Contact?.today || [],
                calculateTotal(dashboardData?.Contact?.today || []),
                false,
                'gray'
              )}
              {renderTaskTable("Overdue Tasks: Contact",
                dashboardData?.Contact?.overdue || [],
                calculateTotal(dashboardData?.Contact?.overdue || []),
                true,
                'gray'
              )}
              {renderTaskTable("This Week Due Tasks: Contact",
                dashboardData?.Contact?.week || [],
                calculateTotal(dashboardData?.Contact?.week || []),
                false,
                'gray'
              )}
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4} mt={4}>
              {renderTaskTable("Today's Due Task: Quotation",
                dashboardData?.Quotation?.today || [],
                calculateTotal(dashboardData?.Quotation?.today || []),
                false,
                'indigo'
              )}
              {renderTaskTable("Overdue Tasks: Quotation",
                dashboardData?.Quotation?.overdue || [],
                calculateTotal(dashboardData?.Quotation?.overdue || []),
                true,
                'indigo'
              )}
              {renderTaskTable("This Week Due Tasks: Quotation",
                dashboardData?.Quotation?.week || [],
                calculateTotal(dashboardData?.Quotation?.week || []),
                false,
                'indigo'
              )}
            </SimpleGrid>

       
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <>

      {/* Tabs with sticky header */}
      <Tabs index={tabIndex} onChange={handleTabChange}>
        <TabList
          className="gap-2 !border-b-0"
          position="sticky"
          top="0"
          zIndex="10"
          bg="white"
        >
          <Tab
            font
          >
            Task  Dashboard
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel p={0}>
            {/* Main scrollable content area */}
            <Box
              height="calc(100vh - 60px)"
              overflowY="auto"
              sx={{
                '&::-webkit-scrollbar': {
                  width: '10px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '5px',
                  '&:hover': {
                    background: '#555',
                  },
                },
              }}
            >
              <Container maxW="container.xl" py={8} px={4} pb={12}>


                {renderContent()}
              </Container>
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </>
  );
};

export default TaskDashboard;